// ============================================================================
// Quiz Types
// ============================================================================

export type QuizType = "gated linear" | "linear";

export type QuizQuestionInputType =
  | "text input"
  | "longtext"
  | "multi-select"
  | "multiple choice"
  | "select"
  | "checkbox"
  | "slider"
  | "organization selector"
  | "yes/no-buttons"
  | "yes/no"
  | "image"
  | "file"
  | "url";

/**
 * Question type groupings for answer payload mapping.
 * Used to determine which answer key to use when saving/loading answers.
 */
export const TEXT_ANSWER_TYPES: QuizQuestionInputType[] = [
  "text input",
  "longtext",
  "url",
];

export const CHOICE_ANSWER_TYPES: QuizQuestionInputType[] = [
  "multi-select",
  "multiple choice",
  "select",
  "checkbox",
  "slider",
  "yes/no-buttons",
  "yes/no",
];

export const FILE_ANSWER_TYPES: QuizQuestionInputType[] = ["image", "file"];

export const AFFILIATION_ANSWER_TYPES: QuizQuestionInputType[] = [
  "organization selector",
];

export type QuizQuestionInputValidationRules = {
  correct_answer?: number;
  max_select?: number | "none";
  min_select?: number;
  max_characters?: number;
  min_characters?: number;
  min_value?: number;
  max_value?: number;
  // File-based answer validation rules
  allowed_file_types?: string[];
  max_file_size?: number;
  min_files?: number;
  max_files?: number;
};

// ============================================================================
// Question Config Object Types
// ============================================================================
// The config_object JSON field stores display/behavior configuration per question.
// Different question types use different config properties.

/**
 * Base config properties shared across all question types.
 */
type QuizQuestionConfigBase = {
  /** Placeholder text for input fields */
  placeholder?: string;
  /** For a question on an intake form: which of the form's screens it is on */
  intake_step?: number;
  /**
   * For a conditional question on an intake form: shown but disabled while
   * its condition is unmet, rather than hidden, so it can be seen waiting
   * on the answer it depends on.
   */
  intake_when_not_asked?: "disabled";
};

/**
 * Config for "yes/no" question type.
 * Used to create acknowledgment-style questions that force a "Yes" answer.
 */
export type YesNoQuestionConfig = QuizQuestionConfigBase & {
  /**
   * When true, renders ForceYesInput component instead of YesNoButtons.
   * The user must click to acknowledge/agree before proceeding.
   */
  force_yes?: boolean;
};

/**
 * Config for "multiple choice" question type.
 * Controls how choices are displayed and selected.
 */
export type MultipleChoiceQuestionConfig = QuizQuestionConfigBase & {
  /**
   * How to render the choices:
   * - "checkbox": RadioGroup component (default)
   * - "select": Select dropdown component
   * - "cloud": SelectChips component (tag cloud style)
   */
  display_type: "checkbox" | "select" | "cloud";
  /**
   * Additional options for the question (rarely used, prefer choices array).
   */
  options?: { title: string }[];
};

/**
 * Config for "longtext" question type.
 * Controls character/word counting and limits.
 */
export type LongtextQuestionConfig = QuizQuestionConfigBase & {
  /**
   * Show current character count below the textarea.
   * Displays as "42 characters" or "42 / 500 characters" if max_characters is set.
   */
  show_character_count?: boolean;
  /**
   * Show current word count below the textarea.
   * Displays as "150 words" or "150 / 300 words" if max_words is set.
   */
  show_word_count?: boolean;
  /**
   * Maximum word limit for validation.
   * When exceeded, count display turns red and validation fails.
   * Note: This is mutually exclusive with max_characters in validation_rules.
   */
  max_words?: number;
};

/**
 * Config for "text input" and "url" question types.
 * Simple text input with optional placeholder.
 */
export type TextInputQuestionConfig = QuizQuestionConfigBase;

/**
 * Combined type for all question config variations.
 * This is a union of all possible config shapes.
 *
 * Note: The API returns a single config_object JSON field, so this type
 * represents all possible properties. Specific question types only use
 * their relevant subset of properties.
 */
export type QuizQuestionInputConfig = QuizQuestionConfigBase & {
  // Yes/No config
  force_yes?: boolean;
  // Multiple choice config
  display_type?: "checkbox" | "select" | "cloud";
  options?: { title: string }[];
  // Longtext config
  show_character_count?: boolean;
  show_word_count?: boolean;
  max_words?: number;
};

export type QuizQuestionLogic = {
  depends_on: number;
  operator: "=" | "!=" | ">" | "<" | ">=" | "<=";
  value: number | string | boolean;
};

export type QuizQuestionChoice = {
  additional_info: string;
  display_order: number;
  display_text: string;
  display_description?: string;
  display_type: string;
  id: number;
  image: string;
  is_default: boolean;
};

/**
 * Media upload object returned from the API for quiz answers.
 * Contains full media details including alt_text for accessibility.
 */
export type QuizUploadedMedia = {
  id: number;
  file: string;
  name: string;
  size: number;
  uploaded_at: string;
  file_type: string;
  alt_text: string;
  organization?: number | null;
  originator?: string;
  main_logo?: boolean;
  uploaded_by?: {
    name: string;
    email: string;
  };
};

/**
 * Affiliation object returned per-question from the quiz response API.
 * Mirrors UserOrganizationAffiliation from the affiliations feature.
 */
export type QuizUserAffiliation = {
  id: number;
  organization: { id: number; name: string; nickname: string | null };
  affiliation_role: { id: number; name: string; display_order: number; is_participant_created: boolean };
  display_order: number;
  date_created: string;
  date_updated: string;
  is_used_in_quiz?: boolean;
};

export type QuizQuestion = {
  id: number;
  display_order: number;
  type: QuizQuestionInputType;
  internal_name: string;
  internal_status: string;
  prompt: string;
  additional_info: string;
  required: boolean;
  header: string;
  choices: QuizQuestionChoice[];
  user_answer?: number[];
  user_answer_text?: string;
  /** Full media objects with alt_text, or array of IDs for backwards compatibility */
  user_uploaded_media?: QuizUploadedMedia[];
  /** Per-question affiliation objects for "organization selector" questions */
  user_affiliations?: QuizUserAffiliation[];
  answered?: boolean;
  config_object: QuizQuestionInputConfig;
  editable: boolean;
  image?: string;
  question_logic: QuizQuestionLogic[] | QuizQuestionLogic | null;
  validation_rules: QuizQuestionInputValidationRules;
  maximum_answers?: number;
};

export type QuizQuestionSet = {
  header: string;
  description?: string;
  questions: QuizQuestion[];
};

/**
 * A quiz's own settings, as published with it: the copy shown around it.
 * Each is optional; a page shows its own wording for one a quiz leaves out.
 */
export type QuizSettings = {
  audience_label?: string;
  step_intro?: string;
  submit_label?: string;
  confirmation_heading?: string;
  confirmation_body?: string;
};

export type Quiz = {
  id: string;
  name: string;
  type: QuizType;
  title?: string;
  description?: string;
  config_object?: QuizSettings;
  isActive: boolean;
  image: string | null;
  internal_status: string;
  date_created: string;
  date_updated: string;
  created_user: number;
  updated_user: number;
  questionSets: QuizQuestionSet[];
};

export type QuizQuestionAnswerKey =
  | "selected_quiz_choices"
  | "answer_text"
  | "uploaded_media"
  | "selected_affiliations";

export type QuizQuestionAnswer = number[] | string;

export type QuizAnswerPayload = {
  quiz_response: number;
  question: number;
  selected_quiz_choices?: number[];
  answer_text?: string;
  uploaded_media?: number[];
  selected_affiliations?: number[];
};

export type QuizFormData = Record<string, QuizQuestionAnswer>;

export type QuizResponse = {
  id: number;
  quiz: {
    id: number;
    name: string;
    description: string;
  };
  title?: string;
  description?: string;
  type?: QuizType;
  status: string;
  time_completed: string;
  time_last_updated: string;
  time_started: string;
  questions: QuizQuestion[];
  questionSets: QuizQuestionSet[];
};

export type QuizResponseWithAnswers = {
  quiz_response: QuizResponse;
  quiz: Quiz;
};

// ============================================================================
// Quiz Form Field Types
// ============================================================================

/**
 * Component types supported by the quiz form.
 * This extends the standard form components with quiz-specific ones.
 */
export type QuizFormComponent =
  | "Input"
  | "Textarea"
  | "Select"
  | "RadioGroup"
  | "YesNoButtons"
  | "SelectChips"
  | "ForceYesInput"
  | "AffiliationListContainer"
  | "FileAnswerInput"
  | "ImageField";

/**
 * Label variant type for quiz form fields.
 * Matches the TypeVariant from themes but defined here to avoid theme dependency.
 */
export type QuizLabelVariant =
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  | "body1"
  | "body2"
  | "caption"
  | "overline";

/**
 * Quiz field type - represents a dynamically generated form field from quiz questions.
 * This is used internally by the quiz feature for dynamic field generation.
 */
export type QuizFieldType = {
  id: number;
  name: string;
  label: string;
  component: QuizFormComponent;
  required?: boolean;
  choices?: QuizQuestionChoice[];
  type?: string;
  validation_rules?: QuizQuestionInputValidationRules;
  image?: string;
  labelVariant?: QuizLabelVariant;
  question_logic?: QuizQuestionLogic;
  options?: Array<{
    label: string;
    value: number;
    description?: string;
  }>;
  maxItems?: number;
  minItems?: number;
  placeholder?: string;
  isForceYes?: boolean;
  isCustomComponent?: boolean;
  displayType?: string;
  props?: Record<string, unknown>;
  // File-based answer field properties
  questionType?: "image" | "file";
  maxFiles?: number;
  /** Initial files with metadata for previously uploaded media */
  initialFiles?: QuizUploadedMedia[];
  /** Help text shown below label */
  helpText?: string;
  /** HTML input type for Input component (e.g., "url", "email") */
  inputType?: string;
  // Longtext-specific props
  /** Whether to show character count below textarea */
  showCharacterCount?: boolean;
  /** Whether to show word count below textarea */
  showWordCount?: boolean;
  /** Maximum word count for longtext validation */
  maxWords?: number;
  /** Maximum character count for longtext validation */
  maxCharacters?: number;
  /** Minimum character count for longtext validation */
  minCharacters?: number;
};

